import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { Reveal, SectionLabel, PageHero } from "@/components/ui-kit";
import { InstagramSection } from "@/components/Sections";
import { ARTICLES } from "@/content/articles";
import { GUIDES } from "@/content/guides";
import { GuideCard } from "@/components/GuideUnlock";
import { usePageMeta } from "@/hooks/usePageMeta";

const CATS = ["All", ...Array.from(new Set(ARTICLES.map((a) => a.category)))];

export const ArticleCard = ({ a, i = 0, featured = false }) => (
  <Reveal delay={(i % 3) * 0.08} className={featured ? "lg:col-span-2" : ""}>
    <Link
      to={`/learn/${a.slug}`}
      data-testid={`article-card-${a.slug}`}
      className={`group flex h-full flex-col overflow-hidden rounded-2xl border border-navy/[0.08] bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-hover ${featured ? "lg:flex-row" : ""}`}
    >
      <div className={`relative overflow-hidden ${featured ? "aspect-[16/10] lg:aspect-auto lg:w-1/2" : "aspect-[16/10]"}`}>
        <img src={a.cover} alt={a.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-navy backdrop-blur">{a.category}</span>
      </div>
      <div className={`flex flex-1 flex-col p-6 ${featured ? "lg:p-10" : ""}`}>
        <p className="flex items-center gap-1.5 text-xs text-ink/50"><Clock className="h-3.5 w-3.5" /> {a.readTime} read</p>
        <h3 className={`mt-3 font-display font-bold leading-snug text-navy group-hover:text-royal ${featured ? "text-2xl sm:text-3xl" : "text-lg"}`}>{a.title}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-ink/65">{a.excerpt}</p>
        <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-royal">Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
      </div>
    </Link>
  </Reveal>
);

export default function LearnPage() {
  const [cat, setCat] = useState("All");
  usePageMeta({
    title: "Learn — Plain-English Guides on Investing, Insurance, Tax & Loans",
    description: "Auxieron's Learn hub: original, jargon-free articles on SIPs, emergency funds, term insurance, tax planning, bonds & REITs, smart borrowing, retirement and asset allocation.",
    path: "/learn",
  });
  const list = cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === cat);

  return (
    <>
      <PageHero
        label="Learn"
        title="Money, explained"
        highlight="in plain English."
        lead="Short, original guides written by our team — no jargon, no hype, no product pushing. Just the ideas you need to make better decisions."
        testId="learn-label"
      />

      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap gap-2" data-testid="learn-filters">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`learn-filter-${c.toLowerCase().replace(" ", "-")}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  cat === c ? "bg-navy text-white" : "border border-navy/10 bg-lightbg text-navy/75 hover:border-navy/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {list.map((a, i) => <ArticleCard key={a.slug} a={a} i={i} featured={cat === "All" && i === 0} />)}
          </div>
        </div>
      </section>

      <section id="guides" className="bg-lightbg py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel testId="guides-label">Free downloadable guides</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Checklists you can print and tick off.
            </h2>
            <p className="mt-4 max-w-2xl text-base text-ink/65 sm:text-lg">
              Four branded PDF checklists distilled from our guides. Enter your details once and download instantly.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {GUIDES.map((g, i) => (
              <Reveal key={g.slug} delay={(i % 4) * 0.08}><GuideCard guide={g} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-6 rounded-[2rem] border border-navy/[0.08] bg-lightbg/60 p-8 sm:flex-row sm:items-center sm:p-10">
              <div>
                <SectionLabel testId="learn-calc-label">Try the numbers</SectionLabel>
                <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Reading is good. Running your own numbers is better.</h2>
              </div>
              <Link to="/calculators" data-testid="learn-calc-cta" className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0d2c56]">
                Open calculators <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <InstagramSection />
    </>
  );
}
